import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Art } from '../components/Art';
import { BottomNav, Button, IconTile, SectionHead, Sheet, VehicleTile, sheetText } from '../components/ui';
import { Icon } from '../components/Icon';
import { Screen } from '../components/Screen';
import { Stage } from '../components/Stage';
import { header_home } from '../art/art';
import { VehicleArt } from '../art/Vehicle';
import { MapView } from '../components/MapView';
import { useLayout } from '../lib/layout';
import { MapPill, VehicleDisc, YouDot } from './mapBits';
import { useApp } from '../lib/app';
import { greeting, initials, km, relativeDay, rupees } from '../lib/format';
import { tripParams } from '../lib/tripUrl';
import { useRecentPlaces } from '../lib/useRecents';
import { finishLater } from '../lib/live';
import { DEFAULT_PICKUP, shortName } from '../lib/places';
import { addressOf, namePoint } from '../lib/placesApi';
import { fareFor, getQuote } from '../lib/quotes';
import { useRide } from '../lib/ride';
import { isOpen, screenFor } from '../lib/rideScreen';
import type { Contact, Nearby, Place, Quote, Ride, SavedPlace, VehicleType } from '../lib/types';
import type { T } from '../lib/i18n';
import { GRID_ORDER, known, words } from '../lib/vehicles';
import { useT } from '../lib/i18n';

/** `quote`: undefined while it loads, null when the trip couldn't be priced. */
type Card = { place: Place; title: string; sub: string; quote: Quote | null | undefined; kind: 'HOME' | 'WORK' | 'RECENT' };

const placeOf = (p: SavedPlace): Place => ({ lat: p.lat, lng: p.lng, address: p.address });
const listNames = (names: string[], and: string) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`);

/** What the open ride is doing, in a line for the card on Home. */
function liveLine(t: T, r: Ride): string {
  const w = words(t, r.vehicleType);
  const name = r.driver?.firstName ?? t('common.yourDriver');
  switch (screenFor(r.status)) {
    case 'finding':
      return t('home.live.finding', { noun: w.noun, type: w.label });
    case 'arriving':
      return r.status === 'ARRIVED' ? t('home.live.here', { name }) : t('home.live.coming', { name });
    case 'ontrip':
      return t('home.live.trip', { place: shortName(r.drop.address) });
    default:
      return t('home.live.done');
  }
}

const OPENED = 'zr-opened';
const openedOnce = (id: string) => {
  try {
    if (sessionStorage.getItem(OPENED) === id) return true;
    sessionStorage.setItem(OPENED, id);
  } catch {
    /* ignore */
  }
  return false;
};

export function Home() {
  const { api, rider, draft, setDraft, refreshRider, locate, here } = useApp();
  const { ride } = useRide();
  const t = useT();
  const nav = useNavigate();
  const [nearby, setNearby] = useState<Nearby | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [quotes, setQuotes] = useState<Record<string, Quote | null>>({});
  const [nameOpen, setNameOpen] = useState(false);
  const [name, setName] = useState('');
  const L = useLayout();
  const recents = useRecentPlaces(4);

  // a ride that is on (or waiting for pay & rate) opens by itself the first time this tab sees it — a refresh or a
  // reopened app lands back on it; after the rider steps Back to Home it stays a card here instead
  const live = ride && (isOpen(ride.status) || (ride.status === 'COMPLETED' && finishLater.get() === ride.id)) ? ride : null;
  useEffect(() => {
    if (live && !openedOnce(live.id)) nav({ to: '/ride' });
  }, [live, nav]);

  // the pickup starts where the phone is (Uber-style): asked once per visit, and quietly every time when the
  // rider already allowed it; if they say no, the last pickup stays
  const asked = useRef(false);
  useEffect(() => {
    if (here || asked.current) return;
    asked.current = true;
    let first = true;
    try {
      first = !sessionStorage.getItem('zr-located');
      sessionStorage.setItem('zr-located', '1');
    } catch {
      /* ignore */
    }
    const go = () =>
      void locate().then((at) => {
        if (!at) return;
        // Ola's address for where the phone is (the nearest landmark when search is unavailable)
        void namePoint(api, at).then((n) => !n.outside && setDraft({ pickup: { ...at, address: addressOf(n) }, pickupIsHere: true }));
      });
    if (first) return go();
    navigator.permissions?.query({ name: 'geolocation' }).then((p) => p.state === 'granted' && go(), () => undefined);
  }, [api, here, locate, setDraft]);

  useEffect(() => {
    if (rider && !rider.name) setNameOpen(true);
  }, [rider]);

  const pickup = draft.pickup;
  useEffect(() => {
    let live = true;
    const load = () => api.nearby(pickup).then((n) => live && setNearby(n), () => undefined);
    void load();
    const t = window.setInterval(load, 15_000);
    api.contacts().then((c) => live && setContacts(c), () => undefined);
    return () => {
      live = false;
      window.clearInterval(t);
    };
  }, [api, pickup]);

  const home = rider?.places.find((p) => p.label === 'HOME') ?? null;
  const work = rider?.places.find((p) => p.label === 'WORK') ?? null;
  // the rider's own last drop-offs (no made-up places): the newest is the card, the rest are a list on wide screens
  const recent = recents?.[0] ?? null;
  const moreRecent = recents?.slice(1) ?? [];

  const targets = useMemo(() => {
    const t: { key: string; place: Place }[] = [];
    if (home) t.push({ key: 'HOME', place: placeOf(home) });
    if (work) t.push({ key: 'WORK', place: placeOf(work) });
    (recents ?? []).forEach((r, i) => t.push({ key: `RECENT${i}`, place: r.place }));
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home?.lat, home?.lng, work?.lat, work?.lng, JSON.stringify(recents?.map((r) => [r.place.lat, r.place.lng]))]);

  useEffect(() => {
    let live = true;
    for (const { key, place } of targets)
      getQuote(api, pickup, place).then(
        (q) => live && setQuotes((m) => ({ ...m, [key]: q })),
        () => live && setQuotes((m) => ({ ...m, [key]: null })),
      );
    return () => {
      live = false;
    };
  }, [api, pickup, targets]);

  // a saved or recent place skips Search: straight to the ride options for it
  const go = (place: Place, q: Quote | null) => {
    setDraft({ drop: place, quote: q });
    nav({ to: '/choose', search: tripParams(pickup, place) });
  };

  const card = (kind: Card['kind']): Card | null => {
    if (kind === 'HOME' && home) return { place: placeOf(home), title: home.name, sub: shortName(home.address), quote: quotes.HOME, kind };
    if (kind === 'WORK' && work) return { place: placeOf(work), title: work.name, sub: shortName(work.address), quote: quotes.WORK, kind };
    if (kind === 'RECENT' && recent) return { place: recent.place, title: shortName(recent.place.address), sub: relativeDay(recent.at), quote: quotes.RECENT0, kind };
    return null;
  };
  const heroCard = card('HOME');
  const workCard = card('WORK');
  const recentCard = card('RECENT');
  /** The fare as text (for labels); empty while it loads or when it couldn't be priced. */
  const fareLabel = (q: Quote | null | undefined) => {
    const f = fareFor(q ?? null, 'AUTO');
    return f != null ? rupees(f) : '';
  };
  /** The fare, or a placeholder the same size while it is on its way (nothing moves when it lands). */
  const autoFare = (q: Quote | null | undefined, w = 44) =>
    q === undefined ? <span aria-hidden="true" className="zr-skeleton" style={{ display: 'inline-block', width: w, height: 16, borderRadius: 8, verticalAlign: 'middle' }} /> : fareLabel(q);
  const dist = (c: Card) => (c.quote ? ` · ${km(c.quote.distanceMeters)}` : '');

  const types: VehicleType[] = useMemo(() => {
    const q = Object.values(quotes).find(Boolean);
    const list = q ? known(q.options).map((o) => o.vehicleType) : (['BIKE', 'AUTO', 'CAR'] as VehicleType[]);
    return [...list].sort((a, b) => GRID_ORDER.indexOf(a) - GRID_ORDER.indexOf(b));
  }, [quotes]);
  const eta = (v: VehicleType) => nearby?.types.find((x) => x.vehicleType === v)?.etaMinutes ?? null;

  const pickType = (v: VehicleType) => {
    setDraft({ vehicleType: v });
    if (draft.drop) nav({ to: '/choose', search: tripParams(pickup, draft.drop) });
    else nav({ to: '/search' });
  };

  const sharingOn = !!rider?.shareTripsAfterDark;
  const names = contacts.map((c) => c.name);

  const cardBox = { flex: 1, minHeight: 0, borderRadius: 24, background: 'var(--s1)', padding: '14px 14px 12px', boxSizing: 'border-box' as const, display: 'flex', flexDirection: 'column' as const, justifyContent: 'space-between', color: 'var(--ink)', textDecoration: 'none', border: 0, textAlign: 'left' as const, cursor: 'pointer', fontFamily: 'inherit', width: '100%' };

  return (
    <Screen name="home" label="Home">
      {L.wide && (
        // tablet / desktop: the shared map shows the pickup and the drivers around it
        <MapView label={t('home.pickupAria', { place: shortName(pickup.address) })} center={pickup} zoom={15}>
          {(nearby?.points ?? []).slice(0, 14).map((p, i) => (
            <VehicleDisc key={`${p.vehicleType}-${i}`} at={p} type={p.vehicleType} size={34} ring={0} glide={1400} />
          ))}
          <YouDot at={pickup} />
          <MapPill at={pickup} side="right" gap={16}>
            {draft.pickupIsHere ? t('loc.current') : shortName(pickup.address)}
          </MapPill>
        </MapView>
      )}
      <div className="zr-scroll">
        <Stage top={0} height={108}>
          <Art piece={header_home} />
        </Stage>
        <div style={{ position: 'relative', margin: 'calc(var(--sat) + 10px) 20px 0', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              type="button"
              aria-label={t('home.pickupAria', { place: shortName(pickup.address) })}
              onClick={() => nav({ to: '/search', search: { field: 'pickup' } })}
              className="zr-hit"
              style={{ height: 40, padding: '0 14px 0 10px', border: 0, borderRadius: 20, background: 'var(--glass)', WebkitBackdropFilter: 'blur(16px)', backdropFilter: 'blur(16px)', boxShadow: 'inset 0 0 0 1px var(--glassLine)', color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', maxWidth: 'calc(100% - 60px)' }}
            >
              <Icon name={draft.pickupIsHere ? 'locate' : 'pin'} size={18} style={{ color: 'var(--acText)', flex: 'none' }} />
              <span style={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortName(pickup.address ?? DEFAULT_PICKUP.name)}</span>
              <Icon name="chevronDown" size={16} sw={2.2} style={{ color: 'var(--ink2)', flex: 'none' }} />
            </button>
            {/* tablets and desktops have the rider in the top bar (a landscape phone has none) */}
            {(!L.wide || L.compact) && <a
              href="/account"
              onClick={(e) => (e.preventDefault(), nav({ to: '/account' }))}
              aria-label={t('home.account')}
              style={{ width: 44, height: 44, borderRadius: 22, background: 'var(--pri)', color: 'var(--priInk)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 900, letterSpacing: '-0.02em', textDecoration: 'none', boxShadow: '0 0 0 3px var(--bg)' }}
            >
              {initials(rider?.name) || <Icon name="user" size={20} />}
            </a>}
          </div>

          <p style={{ margin: '22px 0 0', fontSize: 15, lineHeight: '20px', fontWeight: 500, color: 'var(--ink2)' }}>{greeting(t, rider?.name)}</p>
          <h1 style={{ margin: '2px 0 0', fontSize: 38, lineHeight: '42px', fontWeight: 900, letterSpacing: '-0.04em' }}>{t('home.title')}</h1>

          {live && (
            // the ride that is on, one tap away (after the rider stepped Back from it)
            <button type="button" onClick={() => nav({ to: '/ride' })} data-testid="home-live" aria-label={t('home.live.aria', { status: liveLine(t, live) })} className="zr-press-soft zr-card-btn zr-fadein" style={{ marginTop: 16, minHeight: 64, border: 0, borderRadius: 22, background: 'var(--acSoft)', boxShadow: 'inset 0 0 0 1.5px var(--sel)', padding: '0 12px 0 14px', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink)', textAlign: 'left', fontFamily: 'inherit' }}>
              <span aria-hidden="true" style={{ flex: 'none', width: 10, height: 10, borderRadius: 5, background: 'var(--ac)', boxShadow: '0 0 0 4px var(--acSoft)', animation: 'zr-breathe 2.4s ease-in-out infinite' }} />
              <span className="zr-ellipsis" style={{ flex: 1, minWidth: 0, fontSize: 15, lineHeight: '20px', fontWeight: 700 }}>{liveLine(t, live)}</span>
              <span style={{ flex: 'none', height: 36, padding: '0 14px', borderRadius: 18, background: 'var(--pri)', color: 'var(--priInk)', display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 700 }}>{t('home.live.open')}</span>
            </button>
          )}

          <div style={{ marginTop: 18, height: 60, borderRadius: 30, background: 'var(--s1)', color: 'var(--ink3)', display: 'flex', alignItems: 'center', gap: 12, padding: '0 8px 0 22px' }}>
            <button
              type="button"
              onClick={() => nav({ to: '/search' })}
              style={{ flex: 1, minWidth: 0, height: 60, border: 0, padding: 0, background: 'transparent', color: 'var(--ink3)', display: 'flex', alignItems: 'center', gap: 12, cursor: 'text', textAlign: 'left' }}
            >
              <Icon name="search" size={20} sw={2.2} style={{ color: 'var(--ink)', flex: 'none' }} />
              <span className="zr-ellipsis" style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 500 }}>{t('home.search')}</span>
            </button>
            {/* rides are "now" only (booking for later isn't built): a label, not a control */}
            <span aria-hidden="true" style={{ flex: 'none', height: 44, padding: '0 14px', borderRadius: 22, background: 'var(--bg)', color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700 }}>
              <Icon name="clock" size={16} />
              {t('common.now')}
            </span>
          </div>

          <div style={{ marginTop: 14, height: 204, display: 'grid', gridTemplateColumns: 'minmax(0, 1.12fr) minmax(0, 1fr)', gap: 10 }}>
            <button
              type="button"
              onClick={() => (heroCard ? go(heroCard.place, heroCard.quote ?? null) : nav({ to: '/search', search: { field: 'home' } }))}
              aria-label={heroCard ? `${t('home.bookTo', { place: heroCard.title })}${fareLabel(heroCard.quote) ? `, ${fareLabel(heroCard.quote)}` : ''}` : t('home.setHome')}
              className="zr-press-soft"
              style={{ position: 'relative', overflow: 'hidden', border: 0, borderRadius: 28, background: 'linear-gradient(155deg, var(--heroA), var(--heroB))', boxShadow: 'inset 0 0 0 1px var(--heroLine)', padding: 16, boxSizing: 'border-box', color: 'var(--ink)', textAlign: 'left', display: 'flex', flexDirection: 'column', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              <span style={{ fontSize: 11, lineHeight: '14px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--acText)' }}>{heroCard ? t('home.usually') : t('home.setup')}</span>
              <span style={{ marginTop: 8, fontSize: 26, lineHeight: '28px', fontWeight: 900, letterSpacing: '-0.03em' }}>{heroCard?.title ?? t('home.home')}</span>
              <span style={{ marginTop: 2, fontSize: 13, lineHeight: '17px', fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                {heroCard ? `${heroCard.sub}${dist(heroCard)}` : t('home.saveHome')}
              </span>
              <VehicleArt type="AUTO" width={132} height={80} style={{ position: 'absolute', right: -14, top: 84 }} />
              <span style={{ marginTop: 'auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                <span style={{ fontSize: 22, lineHeight: '26px', fontWeight: 900, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{heroCard ? autoFare(heroCard.quote, 56) : ''}</span>
                <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 22, background: 'var(--pri)', color: 'var(--priInk)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={heroCard ? 'arrow' : 'plus'} size={20} sw={2.3} />
                </span>
              </span>
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
              <button type="button" className="zr-press-soft" onClick={() => (workCard ? go(workCard.place, workCard.quote ?? null) : nav({ to: '/search', search: { field: 'work' } }))} style={cardBox}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 12, background: 'var(--s2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="briefcase" size={18} sw={1.9} />
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{workCard ? autoFare(workCard.quote) : ''}</span>
                </span>
                <span style={{ width: '100%', minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 16, lineHeight: '20px', fontWeight: 700, letterSpacing: '-0.01em' }}>{workCard?.title ?? t('home.work')}</span>
                  <span style={{ display: 'block', marginTop: 1, fontSize: 12.5, lineHeight: '16px', fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {workCard ? `${workCard.sub}${dist(workCard)}` : t('home.saveWork')}
                  </span>
                </span>
              </button>
              {recentCard ? (
                <button type="button" className="zr-press-soft" onClick={() => go(recentCard.place, recentCard.quote ?? null)} style={cardBox}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 12, background: 'var(--s2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="clock" size={18} sw={1.9} />
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{autoFare(recentCard.quote)}</span>
                  </span>
                  <span style={{ width: '100%', minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 16, lineHeight: '20px', fontWeight: 700, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{recentCard.title}</span>
                    <span style={{ display: 'block', marginTop: 1, fontSize: 12.5, lineHeight: '16px', fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {recentCard.sub}
                      {dist(recentCard)}
                    </span>
                  </span>
                </button>
              ) : recents == null ? (
                <span aria-hidden="true" className="zr-skeleton" style={{ flex: 1, borderRadius: 24 }} />
              ) : (
                // no trips yet: the other way in is a place on the map
                <button type="button" className="zr-press-soft" onClick={() => nav({ to: '/search' })} style={cardBox}>
                  <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 12, background: 'var(--s2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="search" size={18} sw={1.9} />
                  </span>
                  <span style={{ display: 'block', fontSize: 16, lineHeight: '20px', fontWeight: 700, letterSpacing: '-0.01em' }}>{t('search.whereTo')}</span>
                </button>
              )}
            </div>
          </div>

          {/* tablets and desktops have the room: the rest of the recent places, one tap to the ride options */}
          {L.wide && moreRecent.length > 0 && (
            <>
              <SectionHead style={{ marginTop: 22 }} title={t('home.recent')} />
              <div className="zr-group" style={{ marginTop: 10 }}>
                {moreRecent.map((r, i) => {
                  const q = quotes[`RECENT${i + 1}`];
                  return (
                    <button key={`${r.place.lat},${r.place.lng}`} type="button" className="zr-row zr-press-soft" onClick={() => go(r.place, q ?? null)} style={{ minHeight: 60 }}>
                      <Icon name="clock" size={20} sw={1.9} style={{ flex: 'none', color: 'var(--ink2)' }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span className="zr-ellipsis" style={{ display: 'block', fontSize: 15, lineHeight: '20px', fontWeight: 700 }}>{shortName(r.place.address)}</span>
                        <span className="zr-ellipsis" style={{ display: 'block', fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>
                          {relativeDay(r.at)}
                          {q ? ` · ${km(q.distanceMeters)}` : ''}
                        </span>
                      </span>
                      <span className="zr-num" style={{ fontSize: 15, fontWeight: 700 }}>{autoFare(q)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <SectionHead
            style={{ marginTop: 22 }}
            title={t('home.pick')}
            right={
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--ink2)' }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 4, background: nearby?.total ? 'var(--ac)' : 'var(--ink3)', boxShadow: '0 0 0 3px var(--acSoft)', animation: nearby?.total ? 'zr-breathe 2.4s ease-in-out infinite' : undefined }} />
                {nearby == null ? t('home.looking') : nearby.total ? t.n('home.nearby', nearby.total) : t('home.noneNearby')}
              </span>
            }
          />
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: `repeat(${types.length}, minmax(0, 1fr))`, gap: 8 }}>
            {types.map((v) => {
              const e = eta(v);
              return <VehicleTile key={v} type={v} name={words(t, v).label} sub={e != null ? `${e} ${t('common.min')}` : nearby ? t('home.noneNear') : '…'} onClick={() => pickType(v)} />;
            })}
          </div>
          <button
            type="button"
            onClick={() => nav({ to: '/account' })}
            className="zr-press-soft"
            style={{ marginTop: 14, height: 64, border: 0, borderRadius: 22, background: 'var(--s1)', padding: '0 14px', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <IconTile icon="shield" />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 15, lineHeight: '19px', fontWeight: 700 }}>{sharingOn ? t('home.sharing.on') : t('home.sharing.off')}</span>
              <span className="zr-ellipsis" style={{ display: 'block', fontSize: 12.5, lineHeight: '16px', fontWeight: 500, color: 'var(--ink2)' }}>
                {!names.length ? t('home.sharing.add') : sharingOn ? t('home.sharing.onSub', { names: listNames(names, t('home.and')) }) : t('home.sharing.offSub', { names: listNames(names, t('home.and')) })}
              </span>
            </span>
            <Icon name="chevronRight" size={18} style={{ color: 'var(--ink3)' }} />
          </button>
          <div className="zr-navspace" />
        </div>
      </div>
      <BottomNav active="ride" />
      <Sheet open={nameOpen} onClose={() => setNameOpen(false)} title={t('home.name.title')}>
        <p style={sheetText}>{t('home.name.body')}</p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim()) return;
            await api.updateMe({ name: name.trim() }).catch(() => undefined);
            await refreshRider();
            setNameOpen(false);
          }}
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={t('home.name.label')}
            placeholder={t('home.name.label')}
            maxLength={80}
            className="zr-field"
            style={{ marginTop: 16, fontSize: 17, fontWeight: 700 }}
          />
          <Button type="submit" disabled={!name.trim()} style={{ marginTop: 16 }}>
            {t('common.save')}
          </Button>
        </form>
      </Sheet>
    </Screen>
  );
}
